import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "@/lib/utils";
import { motion } from "framer-motion";
import { Home, MapPin, ArrowRight, CheckCircle2, Smartphone, Calendar, Info, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import MapLocationPicker from "@/components/MapLocationPicker";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";



const MoversBookingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState("new");
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_phone: user.phone || prev.customer_phone,
        customer_email: user.email || prev.customer_email,
      }));
    }
  }, [user]);

  const [formData, setFormData] = useState({
    pickup_address: "",
    dropoff_address: "",
    home_size: "",
    has_stairs_pickup: false,
    has_stairs_dropoff: false,
    packing_needed: false,
    move_date: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    notes: ""
  });

  const homeSizes = [
    { value: "1bed", label: "1 Bedroom", desc: "2-3 rooms" },
    { value: "studio", label: "2 Bedrooms / Studio", desc: "1-5 rooms" },
    { value: "3bed", label: "3 Bedrooms", desc: "6-7 rooms" },
    { value: "4bed", label: "4 Bedrooms", desc: "8-9 rooms" },
    { value: "5bed_plus", label: "5+ Bedrooms", desc: "10+ rooms" }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getEstimate = async () => {
    if (!formData.pickup_address || !formData.dropoff_address || !formData.home_size || !formData.move_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!pickupCoords) {
      toast.error("Please select a valid pickup location on the map");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/movers/estimate`, { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude });
      setEstimate(res.data);
      setStep(2);
    } catch {
      toast.error("Failed to get estimate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const submitBooking = async () => {
    if (!formData.customer_name || !formData.customer_phone || !formData.customer_email || !formData.move_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/movers/booking`, { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude });
      setStep(3);
      toast.success("Moving request submitted!");
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const proceedToPayment = async () => {
    if (!formData.move_date) {
      toast.error("Please select a move date");
      return;
    }

    setPaymentLoading(true);
    try {
      const bookingResponse = await axios.post(`${API}/movers/booking`, { ...formData, pickup_lat: pickupCoords?.latitude, pickup_lng: pickupCoords?.longitude, dropoff_lat: dropoffCoords?.latitude, dropoff_lng: dropoffCoords?.longitude });
      const booking = bookingResponse.data;
      const serviceData = {
        booking_id: booking.id,
        booking_type: "movers",
        amount: estimate.max_price,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        service_type: "movers",
        pickup_address: formData.pickup_address,
        dropoff_address: formData.dropoff_address,
        pickup_coords: pickupCoords,
        dropoff_coords: dropoffCoords,
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API}/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your driver...");
          setStep(3);
        } else {
          toast.error("Payment authorization failed. Please try again.");
        }
        setPaymentLoading(false);
      } else {
        const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      console.error("Authorization error:", error);
      toast.error(getErrorMessage(error, "Payment authorization failed. Please try another payment method."));
      setPaymentLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="movers-booking-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_stackcart-app/artifacts/dhobm21o_DSC_1305-scaled.jpg.webp')" }}
        ></div>
        {/* Dark Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 via-purple-800/85 to-purple-900/80"></div>
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <Home className="w-8 h-8 text-purple-500" />
            </div>
            <h1 className="heading-2 text-white mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>Moving Services</h1>
            <p className="text-lg md:text-xl leading-relaxed text-white" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
              Full-service moving help for home and office relocations. Our professional crews handle your belongings with care.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-container py-12">
        <div className="max-w-3xl mx-auto">
          {/* Moving Details */}
          {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8" data-testid="movers-step-1">
              <h2 className="heading-3 text-emerald-950 mb-6">Moving Details</h2>

              <div className="space-y-6">
                <MapLocationPicker
                  pickupAddress={formData.pickup_address}
                  dropoffAddress={formData.dropoff_address}
                  onPickupChange={(val) => handleInputChange("pickup_address", val)}
                  onDropoffChange={(val) => handleInputChange("dropoff_address", val)}
                  onPickupCoordsChange={setPickupCoords}
                  onDropoffCoordsChange={setDropoffCoords}
                  pickupLabel="Moving From *"
                  dropoffLabel="Moving To *"
                  pickupPlaceholder="Current address"
                  dropoffPlaceholder="New address"
                  serviceType="movers"
                />

                <div>
                  <Label className="text-emerald-950 font-medium mb-3 block">Home Size *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {homeSizes.map((size) => (
                      <button key={size.value} type="button" onClick={() => handleInputChange("home_size", size.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${formData.home_size === size.value ? "border-purple-500 bg-purple-50" : "border-stone-200 hover:border-purple-300"}`}
                        data-testid={`home-size-${size.value}`}>
                        <div className="font-medium text-emerald-950">{size.label}</div>
                        <div className="text-sm text-stone-500">{size.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-emerald-950 font-medium block">Additional Options</Label>
                  <div className="flex items-center gap-3">
                    <Checkbox id="stairs_pickup" checked={formData.has_stairs_pickup} onCheckedChange={(checked) => handleInputChange("has_stairs_pickup", checked)} data-testid="stairs-pickup-checkbox" />
                    <label htmlFor="stairs_pickup" className="text-stone-600">Stairs at current location</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox id="stairs_dropoff" checked={formData.has_stairs_dropoff} onCheckedChange={(checked) => handleInputChange("has_stairs_dropoff", checked)} data-testid="stairs-dropoff-checkbox" />
                    <label htmlFor="stairs_dropoff" className="text-stone-600">Stairs at new location</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox id="packing" checked={formData.packing_needed} onCheckedChange={(checked) => handleInputChange("packing_needed", checked)} data-testid="packing-checkbox" />
                    <label htmlFor="packing" className="text-stone-600">Need packing services</label>
                  </div>
                </div>

                <div>
                  <Label className="text-emerald-950 font-medium mb-2 block">Move Date *</Label>
                  <Input
                    type="date"
                    className="input-style"
                    value={formData.move_date}
                    onChange={(e) => handleInputChange("move_date", e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    data-testid="move-date-input"
                  />
                </div>

                <Button className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full py-6 font-bold" onClick={getEstimate} disabled={loading} data-testid="get-estimate-btn">
                  {loading ? "Getting Estimate..." : "Continue"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
            )}

            {step === 2 && estimate && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 mt-6" data-testid="payment-step">
                <h2 className="text-xl font-bold text-emerald-950 mb-4">Payment</h2>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-stone-600">Estimated Price</p>
                  <p className="text-3xl font-bold text-purple-700">${estimate.max_price.toFixed(2)} <span className="text-sm font-normal text-stone-500">CAD</span></p>
                </div>
                <SavedCardsSelector onSelect={setSelectedCardId} selectedCardId={selectedCardId} />
                <Button className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full py-6 font-bold mt-4" onClick={proceedToPayment} disabled={paymentLoading} data-testid="pay-btn">
                  {paymentLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Pay & Find Movers <ArrowRight className="w-5 h-5 ml-2" /></>}
                </Button>
              </motion.div>
            )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MoversBookingPage;
